/**
 * 主要人员模块
 */

import React, { FC, useEffect, useMemo, useState } from 'react';
import { ProDetailList } from '@bedrock/eed-react';
import { Radio } from '@bedrock/components';
import {
  FormModule,
  FormDescriptions,
  FormItemSupervisorDetailList,
  FormItemSupervisorInputOrNot,
  FormItemSupervisorInputNotReason,
  FormItemPersonName,
  FormItemPersonIdType,
  FormItemPersonId,
  FormItemPositions,
  FormItemAppointmentAt,
  FormItemDataSource,
  SupplierDetailList,
} from '@purchase/supplier';
import { isEmpty } from 'lodash';

type DataSource = 'supplier' | 'thirdParty';

const KeyPersonnelModule: FC<{
  formDetail: any;
  isDiff?: boolean;
}> = ({ formDetail, isDiff = false }) => {
  const [keyPersonnelSource, setKeyPersonnelSource] =
    useState<DataSource>('supplier');

  const vendorSupervisorInfo = formDetail?.vendorSupervisorInfo;
  const hasSupervisorData = !Boolean(vendorSupervisorInfo?.absenceReason);
  const supplierSupervisorCount = useMemo(
    () => vendorSupervisorInfo?.supervisors?.length ?? 0,
    [vendorSupervisorInfo]
  );
  const vendorQccKeyMemberInfosCount = useMemo(
    () => formDetail?.vendorQccKeyMemberInfos?.length ?? 0,
    [formDetail]
  );
  const totalSupervisorCount =
    supplierSupervisorCount + vendorQccKeyMemberInfosCount;

  // 当只有一方有数据时，自动切换到对应的数据源
  useEffect(() => {
    if (supplierSupervisorCount > 0 && vendorQccKeyMemberInfosCount === 0) {
      setKeyPersonnelSource('supplier');
    } else if (
      supplierSupervisorCount === 0 &&
      vendorQccKeyMemberInfosCount > 0
    ) {
      setKeyPersonnelSource('thirdParty');
    }
  }, [supplierSupervisorCount, vendorQccKeyMemberInfosCount]);

  return (
    <FormModule
      title="主要人员"
      titleCount={isDiff ? undefined : totalSupervisorCount}
      proCardProps={{
        expandable: true,
        defaultExpanded: totalSupervisorCount > 0,
      }}
    >
      <Radio.Group
        value={keyPersonnelSource}
        onChange={(e) => setKeyPersonnelSource(e.target.value)}
        className="mb-3"
      >
        <Radio.Button value="supplier">
          供应商录入 ({supplierSupervisorCount})
        </Radio.Button>
        <Radio.Button value="thirdParty">
          第三方获取 ({vendorQccKeyMemberInfosCount})
        </Radio.Button>
      </Radio.Group>

      {keyPersonnelSource === 'supplier' && (
        <>
          {!hasSupervisorData ? (
            <FormDescriptions
              columns={1}
              labelStyle={{ width: 180 }}
              margin={false}
            >
              <FormItemSupervisorInputOrNot
                label="是否上传董监高信息"
                name={['vendorSupervisorInfo', 'inputOrNot']}
              />
              <FormItemSupervisorInputNotReason
                label="缺失原因"
                name={['vendorSupervisorInfo', 'absenceReason']}
              />
            </FormDescriptions>
          ) : (
            <FormItemSupervisorDetailList
              name={['vendorSupervisorInfo', 'supervisors']}
              fieldProps={{ scroll: { x: 1120 } }}
              reminderDisabled
            >
              <ProDetailList.Item
                label="#"
                name="#"
                meta={{ columnProps: { width: 50, fixed: true } }}
              >
                {(value, record, index) => index + 1}
              </ProDetailList.Item>
              <FormItemPersonName
                label="姓名"
                name="name"
                meta={{
                  columnProps: {
                    width: 200,
                    className: 'font-bold',
                    fixed: true,
                  },
                }}
              />
              <FormItemPositions name="positions" label="职务" />
              <FormItemAppointmentAt name="appointmentAt" label="任职日期" />
              <FormItemPersonIdType name="personIdType" label="证件类型" />
              <FormItemPersonId name="personId" label="证件号码" />
            </FormItemSupervisorDetailList>
          )}
        </>
      )}

      {keyPersonnelSource === 'thirdParty' && (
        <SupplierDetailList
          name="vendorQccKeyMemberInfos"
          fieldProps={{ scroll: { x: 1120 } }}
          displayMode="table"
        >
          <ProDetailList.Item
            label="#"
            name="#"
            meta={{ columnProps: { width: 50, fixed: true } }}
          >
            {(value, record, index) => index + 1}
          </ProDetailList.Item>
          <FormItemPersonName
            label="姓名"
            name="name"
            meta={{
              columnProps: { width: 200, className: 'font-bold', fixed: true },
            }}
          />
          <FormItemPositions name="positions" label="职务" />
          <FormItemDataSource
            label="数据来源"
            name="dataSource"
            meta={{ columnProps: { width: 160 } }}
          />
        </SupplierDetailList>
      )}
    </FormModule>
  );
};

export default KeyPersonnelModule;
